import {
  EMPTY_PERMISSION_SELECTION,
  PermissionPlatform,
  PermissionSelection,
} from "./types";

export type PermissionLeaf = {
  value: string;
  label: string;
  description?: string;
};

export type PermissionGroup = {
  id: string;
  label: string;
  platform: PermissionPlatform;
  permissions: readonly PermissionLeaf[];
};

export const CMP_PERMISSION_VALUES = {
  globalSettings: "CHAMBER_ADMIN__MY_CHAMBER__GLOBAL_SETTINGS",
  membershipSettings: "CHAMBER_ADMIN__MY_CHAMBER__MEMBERSHIP_SETTINGS",
  emailAndCommunicationSettings:
    "CHAMBER_ADMIN__MY_CHAMBER__EMAIL_AND_COMMUNICATION_SETTINGS",
  affinityProgramSetup:
    "CHAMBER_ADMIN__MY_CHAMBER__AFFINITY_PROGRAM_SETUP",
  analytics: "CHAMBER_ADMIN__MY_CHAMBER__ANALYTICS",
  manageChamberProfile:
    "CHAMBER_ADMIN__MY_CHAMBER__MANAGE_CHAMBER_PROFILE",
  createProspect: "CHAMBER_ADMIN__PROSPECTS__CREATE_PROSPECT",
  manageProspect: "CHAMBER_ADMIN__PROSPECTS__MANAGE_PROSPECT",
  createMember: "CHAMBER_ADMIN__MEMBERS__CREATE_MEMBER",
  manageMember: "CHAMBER_ADMIN__MEMBERS__MANAGE_MEMBER",
  changeMemberCompanyName: "CHAMBER_ADMIN__MEMBERS__CHANGE_COMPANY_NAME",
  createEvent: "CHAMBER_ADMIN__EVENTS__CREATE_EVENT",
  manageEvent: "CHAMBER_ADMIN__EVENTS__MANAGE_EVENT",
  manageTicketSales: "CHAMBER_ADMIN__EVENTS__MANAGE_TICKET_SALES",
  manageSponsorshipSales:
    "CHAMBER_ADMIN__EVENTS__MANAGE_SPONSORSHIP_SALES",
  createEmail: "CHAMBER_ADMIN__EMAIL__CREATE_EMAIL",
  manageEmails: "CHAMBER_ADMIN__EMAIL__MANAGE_EMAILS",
  createInvoice: "CHAMBER_ADMIN__INVOICING__CREATE_INVOICE",
  manageInvoices: "CHAMBER_ADMIN__INVOICING__MANAGE_INVOICES",
  reports: "CHAMBER_ADMIN__REPORTS",
  surveyEngine: "CHAMBER_ADMIN__SURVEY_ENGINE",
  calculators: "CHAMBER_ADMIN__CALCULATORS",
  websiteBuilderAccess: "CHAMBER_ADMIN__WEBSITE__MANAGE_WEBSITE",
  support: "CHAMBER_ADMIN__SUPPORT",
  viewUserActivityLog: "CHAMBER_ADMIN__VIEW_USER_ACTIVITY_LOG",
} as const;

export const CHAMBER_PERKS_PERMISSION_VALUES = {
  accessChamberDashboard: "CHAMBER_ADMIN__ACCESS_CHAMBER_DASHBOARD",
} as const;

export const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  {
    id: "chamber-set-up",
    label: "Chamber Set Up",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.globalSettings,
        label: "Global Settings",
      },
      {
        value: CMP_PERMISSION_VALUES.membershipSettings,
        label: "Membership Settings",
      },
      {
        value: CMP_PERMISSION_VALUES.emailAndCommunicationSettings,
        label: "Emails & Communication Settings",
      },
      {
        value: CMP_PERMISSION_VALUES.affinityProgramSetup,
        label: "Affinity Program Set Up",
      },
    ],
  },
  {
    id: "my-chamber",
    label: "My Chamber",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.analytics,
        label: "Access Analytics Overview",
      },
      {
        value: CMP_PERMISSION_VALUES.manageChamberProfile,
        label: "Manage Chamber Profile",
      },
    ],
  },
  {
    id: "prospects",
    label: "Prospects",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.createProspect,
        label: "Create Prospects",
      },
      {
        value: CMP_PERMISSION_VALUES.manageProspect,
        label: "Manage Prospects",
      },
    ],
  },
  {
    id: "members",
    label: "Members",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.createMember,
        label: "Create Members",
      },
      {
        value: CMP_PERMISSION_VALUES.manageMember,
        label: "Manage Members",
      },
      // CMP Dashboard (21) CD:11023 (IF11 MB5): after Manage Members, not indented.
      {
        value: CMP_PERMISSION_VALUES.changeMemberCompanyName,
        label: "Change Existing Member Company Name",
      },
    ],
  },
  {
    id: "events",
    label: "Events",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.createEvent,
        label: "Create Events",
      },
      {
        value: CMP_PERMISSION_VALUES.manageEvent,
        label: "Manage Events",
      },
      {
        value: CMP_PERMISSION_VALUES.manageTicketSales,
        label: "Manage Ticket Sales",
      },
      {
        value: CMP_PERMISSION_VALUES.manageSponsorshipSales,
        label: "Manage Sponsorship Sales",
      },
    ],
  },
  {
    id: "emails",
    label: "Emails & Communication",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.createEmail,
        label: "Create Emails",
      },
      {
        value: CMP_PERMISSION_VALUES.manageEmails,
        label: "Manage Emails",
      },
    ],
  },
  {
    id: "invoicing",
    label: "Invoicing",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.createInvoice,
        label: "Create Invoices",
      },
      {
        value: CMP_PERMISSION_VALUES.manageInvoices,
        label: "Manage Invoices",
      },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.reports,
        label: "Access Reports",
      },
    ],
  },
  {
    id: "survey-engine",
    label: "Survey Engine",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.surveyEngine,
        label: "Access Survey Engine",
      },
    ],
  },
  {
    id: "calculators",
    label: "Calculators",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.calculators,
        label: "Access Calculators",
      },
    ],
  },
  {
    id: "website-builder",
    label: "Website Builder",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.websiteBuilderAccess,
        label: "Access Website Builder",
        description:
          "Allows this user to open and manage this chamber's website builder.",
      },
    ],
  },
  {
    id: "support",
    label: "Support",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.support,
        label: "Contact Support",
      },
    ],
  },
  {
    id: "user-activity-log",
    label: "Employee Activity Log",
    platform: "chamberMemberPro",
    permissions: [
      {
        value: CMP_PERMISSION_VALUES.viewUserActivityLog,
        label: "View Employee Activity Log",
      },
    ],
  },
  {
    id: "chamber-perks",
    label: "Chamber Perks App™ Dashboard",
    platform: "chamberPerksAppAdminConsole",
    permissions: [
      {
        value: CHAMBER_PERKS_PERMISSION_VALUES.accessChamberDashboard,
        label: "Access Chamber Perks App™ Dashboard",
        description:
          "Allows this user to sign in to the Chamber Perks App™ Dashboard.",
      },
    ],
  },
];

export const ALL_PERMISSION_LEAVES = PERMISSION_GROUPS.flatMap(
  (group) => group.permissions
);

export const PERMISSION_LABEL_BY_VALUE = new Map(
  ALL_PERMISSION_LEAVES.map((permission) => [
    permission.value,
    permission.label,
  ])
);

const valuesForPlatform = (platform: PermissionPlatform): string[] =>
  PERMISSION_GROUPS.filter((group) => group.platform === platform).flatMap(
    (group) => group.permissions.map((permission) => permission.value)
  );

const ALL_VALUES_BY_PLATFORM: Record<PermissionPlatform, string[]> = {
  chamberMemberPro: valuesForPlatform("chamberMemberPro"),
  chamberPerksAppAdminConsole: valuesForPlatform(
    "chamberPerksAppAdminConsole"
  ),
};

const selectedValuesForGroup = (
  selection: PermissionSelection,
  group: PermissionGroup
): string[] =>
  group.permissions
    .map((permission) => permission.value)
    .filter((value) => selection[group.platform].includes(value));

const replacePlatformValues = ({
  platform,
  selection,
  values,
}: {
  platform: PermissionPlatform;
  selection: PermissionSelection;
  values: ReadonlySet<string>;
}): PermissionSelection => ({
  ...selection,
  [platform]: ALL_VALUES_BY_PLATFORM[platform].filter((value) =>
    values.has(value)
  ),
});

export const createEmptyPermissionSelection = (): PermissionSelection => ({
  ...EMPTY_PERMISSION_SELECTION,
  chamberMemberPro: [],
  chamberPerksAppAdminConsole: [],
});

export const normalizePermissionSelection = (
  value?: Partial<PermissionSelection> | null
): PermissionSelection => ({
  chamberMemberPro: ALL_VALUES_BY_PLATFORM.chamberMemberPro.filter(
    (permission) => value?.chamberMemberPro?.includes(permission)
  ),
  chamberPerksAppAdminConsole:
    ALL_VALUES_BY_PLATFORM.chamberPerksAppAdminConsole.filter((permission) =>
      value?.chamberPerksAppAdminConsole?.includes(permission)
    ),
});

export const togglePermissionLeaf = ({
  checked,
  permission,
  platform,
  selection,
}: {
  checked: boolean;
  permission: string;
  platform: PermissionPlatform;
  selection: PermissionSelection;
}): PermissionSelection => {
  const values = new Set(selection[platform]);

  if (checked) {
    values.add(permission);
  } else {
    values.delete(permission);
  }

  return replacePlatformValues({ platform, selection, values });
};

export const togglePermissionGroup = ({
  checked,
  group,
  selection,
}: {
  checked: boolean;
  group: PermissionGroup;
  selection: PermissionSelection;
}): PermissionSelection => {
  const values = new Set(selection[group.platform]);

  group.permissions.forEach(({ value }) => {
    if (checked) {
      values.add(value);
    } else {
      values.delete(value);
    }
  });

  return replacePlatformValues({
    platform: group.platform,
    selection,
    values,
  });
};

export const isPermissionGroupChecked = (
  selection: PermissionSelection,
  group: PermissionGroup
): boolean =>
  group.permissions.length > 0 &&
  selectedValuesForGroup(selection, group).length === group.permissions.length;

export const isPermissionGroupIndeterminate = (
  selection: PermissionSelection,
  group: PermissionGroup
): boolean => {
  const count = selectedValuesForGroup(selection, group).length;

  return count > 0 && count < group.permissions.length;
};

export const selectAllPermissions = (checked: boolean): PermissionSelection =>
  checked
    ? {
        chamberMemberPro: [...ALL_VALUES_BY_PLATFORM.chamberMemberPro],
        chamberPerksAppAdminConsole: [
          ...ALL_VALUES_BY_PLATFORM.chamberPerksAppAdminConsole,
        ],
      }
    : createEmptyPermissionSelection();

export const isEveryPermissionSelected = (
  selection: PermissionSelection
): boolean =>
  PERMISSION_GROUPS.every((group) =>
    isPermissionGroupChecked(selection, group)
  );

export const isPermissionSelectionIndeterminate = (
  selection: PermissionSelection
): boolean => {
  const count = getSelectedPermissionCount(selection);

  return count > 0 && !isEveryPermissionSelected(selection);
};

export const getSelectedPermissionCount = (
  selection: PermissionSelection
): number =>
  selection.chamberMemberPro.length +
  selection.chamberPerksAppAdminConsole.length;

export const getPermissionLabel = (value: string): string =>
  PERMISSION_LABEL_BY_VALUE.get(value) ?? value;
